<script lang="ts" module>
	declare global {
		interface Window {
			grecaptcha?: {
				execute(widgetId?: string): Promise<void>;
				reset(widgetId?: string): void;
				getResponse(widgetId?: string): string;
				ready(callback: () => void): void;
				render(container: Element, parameters: { sitekey: string; size: "invisible" }): string;
			};
		}
	}
</script>

<script lang="ts">
	import type { Snippet } from "svelte";
	import type { HTMLFormAttributes } from "svelte/elements";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { handleInvalidInputs } from "./handleInvalidInputs.js";

	let {
		/* eslint-disable prefer-const */
		this: form,
		enableRecaptcha = false,
		onsubmit,
		children,
		...rest
		/* eslint-enable prefer-const */
	}: {
		this?: HTMLFormElement;
		enableRecaptcha?: boolean;
		onsubmit?(this: void, event: SubmitEvent): void;
		children: Snippet;
	} & Omit<HTMLFormAttributes, "onsubmit"> = $props();

	const { RECAPTCHA_SITE_KEY } = getGlobalSettings();

	let widgetReady = false;
	let notifyReady: (() => void) | undefined;
	let widgetId: string | undefined;

	function renderWidget(element: HTMLElement): void {
		if (!window.grecaptcha) {
			return;
		}
		widgetId = window.grecaptcha.render(element, { sitekey: RECAPTCHA_SITE_KEY, size: "invisible" });
		window.grecaptcha.ready(() => {
			widgetReady = true;
			notifyReady?.();
		});
	}

	function waitForWidget(): Promise<void> {
		if (widgetReady) {
			return Promise.resolve();
		}
		return new Promise((resolve) => {
			notifyReady = resolve;
		});
	}

	async function handleSubmit(event: SubmitEvent): Promise<void> {
		// No key or unloaded script → submit normally; reCAPTCHA is optional.
		if (!enableRecaptcha || !RECAPTCHA_SITE_KEY || !window.grecaptcha) {
			onsubmit?.(event);
			return;
		}

		event.preventDefault();
		await waitForWidget();
		await window.grecaptcha.execute(widgetId);
		const token = window.grecaptcha.getResponse(widgetId);
		if (token) {
			window.grecaptcha.reset(widgetId);
			onsubmit?.(event);
		}
	}
</script>

<form bind:this={form} {...rest} onsubmit={handleSubmit} use:handleInvalidInputs>
	{#if enableRecaptcha && RECAPTCHA_SITE_KEY}
		<!-- Absolute (not hidden): keeps the anchor out of the grid flow so it doesn't take a
		     cell, but stays displayed — the reCAPTCHA badge is rendered inside it and would be
		     hidden too if we used display:none. -->
		<div use:renderWidget class="absolute"></div>
	{/if}
	{@render children()}
</form>
